import { useState, useRef, useEffect, useLayoutEffect } from 'react';

/**
 * Make an input keep track of its own state for a short time while editing. Intended for controlled
 * inputs that may be slow to update, due to the state update triggering a big re-render.
 *
 * Lazy inputs should be preferred when the input does not need to modify the value in any way. If
 * the value needs to be modified (e.g. trimming whitespace), then the input should not use this
 * hook. Instead it can use a regular Input with a controlled value, or defer triggering onChange
 * to a later time, such as on blur.
 *
 * @note Uncontrolled inputs do not benefit from this as they don't trigger state updates as the
 *   user types.
 *
 * Must be used on a controlled input, so value and onChange are required.
 */
function useLazyInput(props) {
    const [value, setValue] = useState(props.value ?? '');
    const requestedValue = useRef(null);
    const timeout = useRef(null);
    useEffect(() => () => {
        if (timeout.current) {
            clearTimeout(timeout.current);
        }
    }, []);
    useLayoutEffect(() => {
        if (timeout.current == null) {
            setValue(props.value ?? '');
            requestedValue.current = null;
        }
        else {
            requestedValue.current = props.value ?? '';
        }
    }, [props.value]);
    const onChange = (newValue, opts) => {
        if (timeout.current)
            clearTimeout(timeout.current);
        timeout.current = window.setTimeout(() => {
            timeout.current = null;
            setValue(requestedValue.current ?? '');
            requestedValue.current = null;
        }, 1000);
        setValue(newValue);
        props.onChange(newValue, opts);
    };
    return { ...props, value, onChange };
}

export { useLazyInput };
//# sourceMappingURL=use-lazy-input.js.map
